import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getPageUsers, updateUserRole } from "../../api/AdminApi";

const ViewCustomers = () => {
    const navigate = useNavigate();
    const [allCustomers, setAllCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [category] = useState("gvg");
    const [hasMoreData, setHasMoreData] = useState(true);
    const [selectedRole, setSelectedRole] = useState({});
    const [updatingRole, setUpdatingRole] = useState({});

    // Define all available roles
    const availableRoles = [
        "PUPIL",
        "ADMIN",
        "EMPLOYEE",
        "MANAGER"
    ];

    // Fetch ALL customers from backend (load once)
    const fetchAllCustomers = () => {
        setLoading(true);
        getPageUsers(0, category, "", 1000)
            .then((res) => {
                const responseData = res.data || res;
                
                let customersData = [];

                if (Array.isArray(responseData)) {
                    customersData = responseData;
                } else if (responseData.content) {
                    customersData = responseData.content || [];
                } else if (responseData.data) {
                    customersData = responseData.data || [];
                }

                setAllCustomers(customersData);
                setFilteredCustomers(customersData);
                setTotalItems(customersData.length);
                setLoading(false);
                
                if (customersData.length < 1000) {
                    setHasMoreData(false);
                }
            })
            .catch((error) => {
                console.error("Error fetching customers:", error);
                setError(error.message || "Failed to fetch customers");
                setLoading(false);
            });
    };

    // Filter customers based on search text (client-side only)
    const filterCustomers = (text) => {
        if (!text.trim()) {
            setFilteredCustomers(allCustomers);
            setTotalItems(allCustomers.length);
            setCurrentPage(0);
            return;
        }

        const filtered = allCustomers.filter(customer =>
            (customer.name && customer.name.toLowerCase().includes(text.toLowerCase())) ||
            (customer.pupilEmail && customer.pupilEmail.toLowerCase().includes(text.toLowerCase())) ||
            (customer.pupilMobile && customer.pupilMobile.includes(text)) ||
            (customer.aadhar && customer.aadhar.includes(text)) ||
            (customer.pan && customer.pan.toLowerCase().includes(text.toLowerCase())) ||
            (customer.category && customer.category.toLowerCase().includes(text.toLowerCase())) ||
            (customer.status && customer.status.toLowerCase().includes(text.toLowerCase()))
        );

        setFilteredCustomers(filtered);
        setTotalItems(filtered.length);
        setCurrentPage(0);
    };

    // Update role function
    const handleUpdateRole = async (customerEmail, currentRole) => {
        const newRole = selectedRole[customerEmail] || (currentRole === "PUPIL" ? "ADMIN" : "PUPIL");
        
        // Validate role
        if (!["PUPIL", "ADMIN"].includes(newRole)) {
            alert("Invalid role selected");
            return;
        }

        const payload = {
            email: customerEmail,
            role: newRole,
            orgName: "gvg",
        };

        setUpdatingRole(prev => ({ ...prev, [customerEmail]: true }));

        try {
            const response = await updateUserRole(payload);
            console.log("Role updated successfully:", response);
            
            // Update local state to reflect the role change
            setAllCustomers(prevCustomers => 
                prevCustomers.map(customer => 
                    customer.pupilEmail === customerEmail 
                        ? { ...customer, role: newRole }
                        : customer
                )
            );
            
            setFilteredCustomers(prevCustomers => 
                prevCustomers.map(customer => 
                    customer.pupilEmail === customerEmail 
                        ? { ...customer, role: newRole }
                        : customer
                )
            );

            // Clear the selected role
            setSelectedRole(prev => {
                const updated = {...prev};
                delete updated[customerEmail];
                return updated;
            });

            alert(`Role successfully updated to ${newRole} for ${customerEmail}`);

        } catch (error) {
            console.error("Error updating role:", error);
            alert(`Failed to update role: ${error.message || "Unknown error"}`);
        } finally {
            setUpdatingRole(prev => ({ ...prev, [customerEmail]: false }));
        }
    };

    // Handle role selection change
    const handleRoleChange = (customerEmail, role) => {
        setSelectedRole(prev => ({
            ...prev,
            [customerEmail]: role
        }));
    };

    // Get role badge color based on role
    const getRoleBadgeColor = (role) => {
        switch (role) {
            case "ADMIN":
                return "bg-red-100 text-red-800 border border-red-200";
            case "PUPIL":
                return "bg-green-100 text-green-800 border border-green-200";
            case "EMPLOYEE":
                return "bg-blue-100 text-blue-800 border border-blue-200";
            case "MANAGER":
                return "bg-purple-100 text-purple-800 border border-purple-200";
            default:
                return "bg-gray-100 text-gray-800 border border-gray-200";
        }
    };

    // Get role icon based on role
    const getRoleIcon = (role) => {
        switch (role) {
            case "ADMIN":
                return (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                );
            case "PUPIL":
                return (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                );
        }
    };

    useEffect(() => {
        fetchAllCustomers();
    }, []);

    useEffect(() => {
        filterCustomers(searchText);
    }, [searchText, allCustomers]);

    useEffect(() => {
        setCurrentPage(0);
    }, [itemsPerPage]);

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = totalItems > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalItems);
    
    const currentItems = filteredCustomers.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    );

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchText("");
    };

    const handleViewProfile = (customerEmail) => {
        navigate(`/profileviewFromAdmin/${customerEmail}`);
    };

    const handleItemsPerPageChange = (e) => {
        const newSize = parseInt(e.target.value);
        setItemsPerPage(newSize);
    };

    const handleLoadMore = () => {
        console.log("Load more functionality can be implemented here");
    };

    if (loading && allCustomers.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-amber-500 mx-auto mb-4"></div>
                    <p className="text-lg font-semibold text-gray-800">Loading customers...</p>
                    <p className="text-sm text-gray-600 mt-2">Please wait while we fetch the data</p>
                </div>
            </div>
        );
    }

    if (error && allCustomers.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4">
                <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-md text-center border border-gray-200">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mb-4">
                        <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Error loading customers</h3>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={fetchAllCustomers}
                        className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-medium rounded-lg shadow-md hover:from-amber-600 hover:to-amber-700 transition-all duration-200 transform hover:-translate-y-0.5"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-6 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                        Customer Management
                    </h1>
                    <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">
                        View and manage all customer profiles with comprehensive search and role management
                    </p>
                </div>

                {/* Search Card */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 mb-6 overflow-hidden">
                    <div className="p-6">
                        <div className="space-y-6">
                            {/* Search Input */}
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg className="h-6 w-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search by name, email, mobile, Aadhar, PAN, category or status..."
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    className="block w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-300 rounded-xl leading-6 bg-white placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200"
                                />
                                {searchText && (
                                    <button
                                        onClick={handleClearSearch}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center hover:scale-110 transition-transform duration-200"
                                        title="Clear search"
                                    >
                                        <svg className="h-6 w-6 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                            
                            {/* Stats and Controls */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-amber-800">{allCustomers.length}</div>
                                        <div className="text-sm font-medium text-amber-700">Total Customers</div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-800">{filteredCustomers.length}</div>
                                        <div className="text-sm font-medium text-blue-700">
                                            {searchText ? "Search Results" : "Displayed Customers"}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <label htmlFor="itemsPerPage" className="block text-sm font-semibold text-green-800 mb-2">
                                                Items per page
                                            </label>
                                            <select
                                                id="itemsPerPage"
                                                value={itemsPerPage}
                                                onChange={handleItemsPerPageChange}
                                                className="block w-full py-2 px-3 border-2 border-green-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm font-medium transition-all duration-200"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-bold text-green-800">{startItem}-{endItem}</div>
                                            <div className="text-sm font-medium text-green-700">
                                                Page {currentPage + 1} of {Math.max(1, totalPages)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customers List/Table */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                    {/* Mobile Card View */}
                    <div className="sm:hidden">
                        {currentItems.length > 0 ? (
                            <div className="divide-y divide-gray-200">
                                {currentItems.map((customer, index) => (
                                    <div key={customer.id || customer.pupilEmail || index} className="p-6 hover:bg-gradient-to-r hover:from-amber-50 hover:to-amber-100 transition-all duration-300 border-b border-gray-200 last:border-b-0">
                                        <div className="flex justify-between items-start mb-4">
                                            <h3 className="text-lg font-bold text-gray-900 truncate bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                                {customer.name || "No Name"}
                                            </h3>
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getRoleBadgeColor(customer.role || "PUPIL")}`}>
                                                {getRoleIcon(customer.role || "PUPIL")}
                                                {customer.role || "PUPIL"}
                                            </span>
                                        </div>
                                        
                                        <div className="space-y-3 text-sm">
                                            {/* <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-semibold text-gray-600">Email:</span>
                                                <span className="text-gray-900 font-medium truncate">{customer.pupilEmail || "-"}</span>
                                            </div> */}
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-semibold text-gray-600">Mobile:</span>
                                                <span className="text-gray-900 font-medium">{customer.pupilMobile || "-"}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-semibold text-gray-600">Aadhar:</span>
                                                <span className="text-gray-900 font-medium truncate ml-2">{customer.aadhar || "-"}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-semibold text-gray-600">PAN:</span>
                                                <span className="text-gray-900 font-medium truncate ml-2">{customer.pan || "-"}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-semibold text-gray-600">Category:</span>
                                                <span className="text-gray-900 font-medium capitalize bg-gray-100 px-2 py-1 rounded">{customer.category || "-"}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                                            {/* Role Selection */}
                                            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                                <span className="text-sm font-semibold text-gray-700">Change Role:</span>
                                                <select
                                                    value={selectedRole[customer.pupilEmail] || customer.role || "PUPIL"}
                                                    onChange={(e) => handleRoleChange(customer.pupilEmail, e.target.value)}
                                                    className="ml-2 py-2 px-3 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all duration-200"
                                                >
                                                    {availableRoles.map((role) => (
                                                        <option key={role} value={role}>
                                                            {role}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            {/* Action Buttons */}
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => handleViewProfile(customer.pupilEmail)}
                                                    className="flex-1 py-3 px-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-4 focus:ring-amber-500/20 transition-all duration-200 transform hover:-translate-y-0.5"
                                                >
                                                    View Profile
                                                </button>
                                                <button
                                                    onClick={() => handleUpdateRole(customer.pupilEmail, customer.role || "PUPIL")}
                                                    disabled={updatingRole[customer.pupilEmail]}
                                                    className={`flex-1 py-3 px-4 border-2 font-semibold rounded-lg focus:outline-none focus:ring-4 transition-all duration-200 transform hover:-translate-y-0.5 ${
                                                        updatingRole[customer.pupilEmail] 
                                                            ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed" 
                                                            : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus:ring-amber-500/20"
                                                    }`}
                                                >
                                                    {updatingRole[customer.pupilEmail] ? (
                                                        <span className="flex items-center justify-center">
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Updating
                                                        </span>
                                                    ) : "Update Role"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-12 text-center">
                                <div className="mx-auto h-24 w-24 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mb-4">
                                    <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                                    {searchText ? "No customers found" : "No customers available"}
                                </h3>
                                <p className="text-gray-500 max-w-sm mx-auto">
                                    {searchText ? "Try adjusting your search terms or filters to find what you're looking for." : "There are currently no customers in the system."}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                                <tr>
                                    {["Name", "Mobile", "Aadhar", "PAN", "Current Role", "New Role", "Actions"].map((header) => (
                                        <th 
                                            key={header}
                                            scope="col" 
                                            className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200"
                                        >
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {currentItems.length > 0 ? (
                                    currentItems.map((customer, index) => (
                                        <tr key={customer.id || customer.pupilEmail || index} 
                                            className="hover:bg-gradient-to-r hover:from-amber-50 hover:to-amber-100 transition-all duration-300 group">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900 group-hover:text-gray-800">
                                                    {customer.name || "-"}
                                                </div>
                                            </td>
                                            {/* <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 truncate max-w-xs">{customer.pupilEmail || "-"}</div>
                                            </td> */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{customer.pupilMobile || "-"}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 font-mono">{customer.aadhar || "-"}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 uppercase font-mono">{customer.pan || "-"}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getRoleBadgeColor(customer.role || "PUPIL")}`}>
                                                    {getRoleIcon(customer.role || "PUPIL")}
                                                    {customer.role || "PUPIL"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <select
                                                    value={selectedRole[customer.pupilEmail] || customer.role || "PUPIL"}
                                                    onChange={(e) => handleRoleChange(customer.pupilEmail, e.target.value)}
                                                    className="w-full py-2 px-3 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all duration-200"
                                                >
                                                    {availableRoles.map((role) => (
                                                        <option key={role} value={role}>
                                                            {role}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <div className="flex justify-center space-x-3">
                                                    <button
                                                        onClick={() => handleViewProfile(customer.pupilEmail)}
                                                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-4 focus:ring-amber-500/20 transition-all duration-200 transform hover:-translate-y-0.5"
                                                    >
                                                        View Profile
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateRole(customer.pupilEmail, customer.role || "PUPIL")}
                                                        disabled={updatingRole[customer.pupilEmail]}
                                                        className={`inline-flex items-center px-4 py-2 border-2 font-semibold rounded-lg focus:outline-none focus:ring-4 transition-all duration-200 transform hover:-translate-y-0.5 ${
                                                            updatingRole[customer.pupilEmail] 
                                                                ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed" 
                                                                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus:ring-amber-500/20"
                                                        }`}
                                                    >
                                                        {updatingRole[customer.pupilEmail] ? (
                                                            <span className="flex items-center">
                                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                Updating
                                                            </span>
                                                        ) : "Update Role"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-12 text-center">
                                            <div className="text-center">
                                                <div className="mx-auto h-16 w-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mb-4">
                                                    <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                                    {searchText ? "No matching customers" : "No customers available"}
                                                </h3>
                                                <p className="text-gray-500">
                                                    {searchText ? "Try different search criteria" : "Check back later for customer data"}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white px-6 py-4 border-t border-gray-200">
                            <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                                <div className="text-sm text-gray-700 font-medium">
                                    Showing <span className="font-bold text-amber-600">{startItem}-{endItem}</span> of{" "}
                                    <span className="font-bold text-gray-900">{totalItems}</span>
                                    {searchText && (
                                        <span className="text-amber-600 ml-2 font-semibold">(filtered results)</span>
                                    )}
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 0}
                                        className={`p-3 rounded-xl transition-all duration-200 ${
                                            currentPage === 0
                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                : "bg-white text-gray-600 hover:bg-amber-50 hover:text-amber-600 border-2 border-gray-300 hover:border-amber-300 transform hover:-translate-y-0.5"
                                        }`}
                                    >
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    
                                    {/* Mobile page indicator */}
                                    <div className="lg:hidden px-4 py-2 bg-gradient-to-r from-amber-50 to-amber-100 border-2 border-amber-200 rounded-xl text-sm font-bold text-amber-700">
                                        {currentPage + 1} / {totalPages}
                                    </div>
                                    
                                    {/* Desktop page numbers */}
                                    <div className="hidden lg:flex space-x-2">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i;
                                            } else if (currentPage <= 2) {
                                                pageNum = i;
                                            } else if (currentPage >= totalPages - 3) {
                                                pageNum = totalPages - 5 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => handlePageChange(pageNum)}
                                                    className={`px-4 py-2 text-sm font-bold rounded-xl border-2 transition-all duration-200 transform hover:-translate-y-0.5 ${
                                                        currentPage === pageNum
                                                            ? "bg-gradient-to-r from-amber-500 to-amber-600 border-amber-500 text-white shadow-lg"
                                                            : "bg-white border-gray-300 text-gray-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
                                                    }`}
                                                >
                                                    {pageNum + 1}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages - 1}
                                        className={`p-3 rounded-xl transition-all duration-200 ${
                                            currentPage === totalPages - 1
                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                : "bg-white text-gray-600 hover:bg-amber-50 hover:text-amber-600 border-2 border-gray-300 hover:border-amber-300 transform hover:-translate-y-0.5"
                                        }`}
                                    >
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Load More Button */}
                    {hasMoreData && filteredCustomers.length === allCustomers.length && (
                        <div className="bg-white px-6 py-4 border-t border-gray-200 text-center">
                            <button
                                onClick={handleLoadMore}
                                className="px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-xl shadow-lg hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-4 focus:ring-amber-500/20 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-xl"
                            >
                                Load More Customers
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewCustomers;